/**
 * Validates if a file is an image
 */
export function isImageFile(file: File): boolean {
  const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
  return validImageTypes.includes(file.type);
}

/**
 * Validates if a file size is within the limit (in MB)
 */
export function isFileSizeValid(file: File, maxSizeMB: number): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}


export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read file as data URL'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Validates and converts an uploaded image file
 */
export async function validateAndConvertImage(
  file: File,
  maxSizeMB: number = 20
): Promise<{ success: true; dataUrl: string } | { success: false; error: string }> {
  // Check if file is an image
  if (!isImageFile(file)) {
    return {
      success: false,
      error: 'Please upload an image file (JPEG, PNG, GIF, WebP, or BMP)',
    };
  }

  // Check file size
  if (!isFileSizeValid(file, maxSizeMB)) {
    return {
      success: false,
      error: `Image size must be less than ${maxSizeMB}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
    };
  }

  try {
    const dataUrl = await fileToDataUrl(file);
    return {
      success: true,
      dataUrl,
    };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to process the image. Please try again.',
    };
  }
}

/**
 * Triggers a file input dialog and returns the selected file
 */
export function selectImageFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp';

    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      resolve(file || null);
    };

    input.oncancel = () => {
      resolve(null);
    };

    input.click();
  });
}

